/**
 * The popped-out cell editor — universal, never opt-in. Every editable cell
 * pops OUT on edit entry and settles BACK on commit.
 *
 *   view              edit, unfocused        edit, FOCUSED
 *   ┌────────┐        ┌────────┐             ┌────────┐   <- the gridcell
 *   │ 12.50  │  ──►   │[12.50 ]│  ─ focus ─► │        │      aria-expanded
 *   └────────┘        └────────┘             └───┬────┘
 *                      OutPortal                 │ Popper(anchorEl=cell)
 *                      inside the cell     ┌─────▼──────────────┐
 *                                          │[12.50            ]│ <body>
 *                                          └────────────────────┘
 *
 * ONE DOM node makes that move — `react-reverse-portal` re-parents it rather
 * than re-creating it, so caret position, IME composition and typed text all
 * survive. Popper owns POSITION only (anchoring · shift · scroll tracking ·
 * portalling); it never touches the node.
 *
 * PRESENTATION ONLY (ruling R-D). No onChange, no commit, no key handling, no
 * apiRef writes. `surface` is a rendering detail, never a behavioural one.
 *
 * `disablePopover` is GONE, and so is the `!rendered` bail it guarded: MUI
 * hydrates `renderEditCell` onto every typed column (gridStringColDef.js:25),
 * so the bail never fired, and both advertised an opt-out policy that the
 * universal-popout ruling does not have.
 */

import { Popper, type PopperProps, Tooltip } from '@mui/material';
import type { Theme as MuiTheme } from '@mui/material/styles';
import { useTheme } from '@mui/system';
import type { GridApiPremium, GridRenderEditCellParams, GridRowId } from '@mui/x-data-grid-premium';
import { GridRowModes } from '@mui/x-data-grid-premium';
import { RiAlertLine } from '@remixicon/react';
import {
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { createHtmlPortalNode, type HtmlPortalNode, InPortal, OutPortal } from 'react-reverse-portal';

import { takeOutOfTabOrder } from '../a11y/tabOrder';
import type { GridColValue } from '../DataGrid';

import { useEditableGridIssue } from './useEditableGridIssue';

type ExtendedGridRenderEditCellParams = Omit<GridRenderEditCellParams, 'colDef'> & {
  colDef: GridRenderEditCellParams['colDef'] & GridColValue;
};

export type PopoverEditCellProps = PropsWithChildren<ExtendedGridRenderEditCellParams> & {
  issue?: { message: string; warning?: boolean };
  instanceGridId: string;
  /** Layer for the floating editor. Defaults to `theme.zIndex.drawer - 1`. */
  editorZIndex?: number;
};

type CellAnchor = NonNullable<Exclude<PopperProps['anchorEl'], HTMLElement | ((element: HTMLElement) => unknown)>>;

type PortalablePopoverEditCell = PopoverEditCellProps & {
  cellAnchor: CellAnchor;
  contentsRef: RefObject<HTMLDivElement | null>;
  editorLabel: string;
  popoverId: string;
  portalNode: HtmlPortalNode;
};

const POPPER_MODIFIERS: NonNullable<PopperProps['modifiers']> = [
  // `bottom-start` puts our top edge at the cell's BOTTOM, so offset back by exactly the cell
  // height to sit ON the cell, and by 2px horizontally so the 1px border straddles the cell
  // edge.
  {
    name: 'offset',
    options: { offset: ({ reference }: { reference: { height: number } }) => [-2, -reference.height] },
  },
  // Overlaying the anchor means the "other side" is never a valid answer.
  { name: 'flip', enabled: false },
  // Clamp on the cross axis only: a pop-out wider than its column must stay on
  // screen, but it must never slide off its own row.
  { name: 'preventOverflow', options: { mainAxis: false, altAxis: true, padding: 16 } },
];

const LIVE_REGION_ID = 'grid-edit-mode-announcer';

/** One polite region per document, created on first edit and never removed — a region announces only mutations made after it is already in the DOM. */
const editModeAnnouncer = (): HTMLElement => {
  const existing = document.getElementById(LIVE_REGION_ID);

  if (existing) return existing;

  const region = document.createElement('div');
  region.id = LIVE_REGION_ID;
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  region.style.cssText =
    'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0';
  document.body.append(region);

  return region;
};

const editorRowNumber = (
  api: ExtendedGridRenderEditCellParams['api'],
  id: ExtendedGridRenderEditCellParams['id']
): number => {
  const visibleIndex = api.getRowIndexRelativeToVisibleRows?.(id);

  return (typeof visibleIndex === 'number' ? visibleIndex : Number(id)) + 1;
};

const DETACHED_CELL_RECT: DOMRect = {
  x: 0,
  y: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  width: 0,
  height: 0,
  toJSON: () => ({}),
};

/**
 * TAS-3905: MUI re-renders a new product row when its create commits, so this cell renders while
 * the OUTGOING cell node is still in the DOM. A node read during render is detached one commit
 * later, and Popper then pins the focused editor to the viewport corner: open, typed into, and
 * invisible. The anchor resolves the cell each time Popper measures it instead.
 */
const useLiveCellAnchor = (resolveCell: () => HTMLElement | null): CellAnchor =>
  useMemo(() => {
    // A cell briefly absent between commits keeps the editor where it was, not in the corner.
    let lastCellRect = DETACHED_CELL_RECT;

    return {
      getBoundingClientRect: () => {
        const cell = resolveCell();
        if (cell?.isConnected) lastCellRect = cell.getBoundingClientRect();
        return lastCellRect;
      },
      get contextElement() {
        return resolveCell() ?? undefined;
      },
    };
  }, [resolveCell]);

/** MUI owns the gridcell node, so the popover relationship is stamped onto it imperatively and taken back off when the cell leaves edit mode. */
const useCellPopoverAria = (resolveCell: () => HTMLElement | null, expanded: boolean, popoverId: string) => {
  useLayoutEffect(() => {
    // Resolved after commit, for the same reason as `useLiveCellAnchor`.
    const cellElement = resolveCell();
    if (!cellElement) return;

    cellElement.setAttribute('aria-haspopup', 'dialog');
    cellElement.setAttribute('aria-expanded', String(expanded));

    if (expanded) {
      cellElement.setAttribute('aria-owns', popoverId);
    } else {
      cellElement.removeAttribute('aria-owns');
    }

    return () => {
      cellElement.removeAttribute('aria-haspopup');
      cellElement.removeAttribute('aria-expanded');
      cellElement.removeAttribute('aria-owns');
    };
  }, [resolveCell, expanded, popoverId]);
};

const namesItself = (editor: HTMLElement): boolean =>
  Boolean(
    editor.getAttribute('aria-label') ||
      editor.getAttribute('aria-labelledby') ||
      editor.getAttribute('title') ||
      editor.textContent?.trim()
  );

/**
 * A row in edit mode renders an editor for EVERY editable column and the un-popped ones are
 * bare `<input>`s already in the tab sequence — so the grid would expose one tab stop per
 * editable column, where APG grid allows exactly one.
 */
const useInlineEditorOutOfTabOrder = (contentsRef: RefObject<HTMLDivElement | null>, popped: boolean) => {
  useLayoutEffect(() => {
    if (popped || !contentsRef.current) return;

    return takeOutOfTabOrder(contentsRef.current);
  }, [contentsRef, popped]);
};

/** The column header stops naming the cell the moment its text is replaced by a BARE input, so that editor carries the field AND the row itself. An editor that already names itself — its own label, or its own text — keeps that name. */
const useEditorLabel = (contentsRef: RefObject<HTMLDivElement | null>, selector: string, label: string) => {
  useLayoutEffect(() => {
    const editor = contentsRef.current?.querySelector<HTMLElement>(selector);

    if (!editor || namesItself(editor)) return;

    editor.setAttribute('aria-label', label);

    return () => editor.removeAttribute('aria-label');
  }, [contentsRef, selector, label]);
};

export default function PopoverEditCell({
  children,
  instanceGridId,
  editorZIndex,
  ...renderParams
}: PopoverEditCellProps) {
  const contentsRef = useRef<HTMLDivElement>(null);
  const portalNode = useMemo(
    () =>
      createHtmlPortalNode({ attributes: { style: 'width: 100%; height: 100%; display: flex; alignItems: center' } }),
    []
  );

  const issue = useEditableGridIssue(renderParams.rowNode.id as number, renderParams.field);

  const resolveCell = useCallback(
    () => renderParams.api.getCellElement(renderParams.id, renderParams.field),
    [renderParams.api, renderParams.id, renderParams.field]
  );
  const cellAnchor = useLiveCellAnchor(resolveCell);

  const popoverId = `popover-portal-node-${renderParams.id}-${renderParams.field}`;
  const editorLabel = `${renderParams.colDef.headerName ?? renderParams.field}, row ${editorRowNumber(renderParams.api, renderParams.id)}`;

  useCellPopoverAria(resolveCell, renderParams.hasFocus, popoverId);
  useInlineEditorOutOfTabOrder(contentsRef, renderParams.hasFocus);
  useEditorLabel(contentsRef, renderParams.colDef.focusableElement ?? 'input', editorLabel);

  return (
    <>
      <InPortal node={portalNode}>
        <div
          ref={contentsRef}
          id={`cell-contents-${instanceGridId}-${renderParams.id}-${renderParams.field}`}
          className="PopoverEditCell flex items-center w-full h-full relative"
          style={{ justifyContent: renderParams.colDef.align, fontSize: '14px' }}
          data-popover-field={renderParams.field}
          data-popover-row={renderParams.id}
          data-popover-grid={instanceGridId}
        >
          {children}
          {issue && (
            <RiAlertLine
              size={18}
              className={`${issue.warning ? 'text-orange-text' : 'text-red-text'}`}
              style={{ position: 'absolute', right: 32 }}
            />
          )}
        </div>
      </InPortal>
      {renderParams.hasFocus ? (
        <FocusedPopoverEditCell
          cellAnchor={cellAnchor}
          contentsRef={contentsRef}
          editorLabel={editorLabel}
          popoverId={popoverId}
          portalNode={portalNode}
          issue={issue ?? undefined}
          instanceGridId={instanceGridId}
          editorZIndex={editorZIndex}
          {...renderParams}
        />
      ) : (
        <OutPortal node={portalNode} />
      )}
    </>
  );
}

const FocusedPopoverEditCell = ({
  cellAnchor,
  contentsRef,
  editorLabel,
  issue,
  popoverId,
  portalNode,
  editorZIndex,
  ...renderParams
}: PortalablePopoverEditCell) => {
  const theme = useTheme<MuiTheme>();

  useEffect(() => {
    const region = editModeAnnouncer();
    region.textContent = `Editing ${editorLabel}`;

    return () => {
      region.textContent = '';
    };
  }, [editorLabel]);

  let borderColor = '#808F48';

  if (issue) {
    borderColor = issue.warning ? theme.palette.warning.main : theme.palette.error.main;
  }

  return (
    <Popper
      open
      anchorEl={cellAnchor}
      placement="bottom-start"
      modifiers={POPPER_MODIFIERS}
      role="presentation"
      // react-aria hides the rest of the document when it opens an overlay; this
      // marks the floating editor as top layer so it survives that sweep.
      data-react-aria-top-layer="true"
      sx={{ zIndex: editorZIndex ?? theme.zIndex.drawer - 1 }}
    >
      <ForwardFocusToEditor
        api={renderParams.api}
        contentsRef={contentsRef}
        field={renderParams.field}
        rowId={renderParams.id}
        selector={renderParams.colDef.focusableElement ?? 'input'}
      >
        <Tooltip title={issue?.message} arrow>
          <div
            // biome-ignore lint/a11y/noAutofocus: required for tab navigation focus management
            autoFocus
            tabIndex={-1}
            // react-aria's `ariaHideOutside` inerts every new `<body>` child while a modal is
            // open; this is its supported opt-out (the same one its toasts use), honoured in
            // both the initial walk and the MutationObserver.
            data-react-aria-top-layer="true"
            id={popoverId}
            style={{
              border: `1px solid ${borderColor}`,
              borderRadius: '12px',
              backgroundColor: theme.palette.background.paper,
              minHeight: renderParams.api.getRootDimensions?.().rowHeight,
              width: renderParams.colDef.computedWidth + 2,
              minWidth: renderParams.colDef.popoutWidth ?? 128,
              boxShadow: '0 10px 16px -3px rgba(20, 21, 26, 0.08), 0 3px 6px -2px rgba(20, 21, 26, 0.05)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <OutPortal node={portalNode} />
          </div>
        </Tooltip>
      </ForwardFocusToEditor>
    </Popper>
  );
};

/**
 * MUI's `Portal` mounts its children in a FOLLOW-UP commit, so a focus effect written outside
 * the portal runs against a DOM with no editor in it yet; this component's layout effect runs
 * in the commit that mounts the editor.
 */
const ForwardFocusToEditor = ({
  api,
  children,
  contentsRef,
  field,
  rowId,
  selector,
}: PropsWithChildren<{
  api: GridApiPremium;
  contentsRef: RefObject<HTMLDivElement | null>;
  field: string;
  rowId: GridRowId;
  selector: string;
}>) => {
  useLayoutEffect(() => {
    const contents = contentsRef.current;

    if (!contents) {
      return;
    }

    const editorIn = () => contents.querySelector<HTMLElement>(selector);

    const enteredEditor = editorIn();

    enteredEditor?.focus({ preventScroll: true });

    /**
     * A cell is entered READY TO REPLACE, not ready to append.
     *
     *   description  "Hello" + "X"  ──▶  "X"      ✓   not "HelloX"
     *   quantity     "1065"  + "7"  ──▶  "7"      ✓   not "10657"
     *
     * Focusing an input parks the caret AFTER its value, so the first keystroke
     * extended what was already there. Every editable cell type enters the same
     * way, so this belongs here rather than in each editor.
     *
     * ONLY on the entry focus. The restore below re-focuses this same editor every
     * time the portal re-parents it, and re-selecting there would turn a keystroke
     * mid-edit into a wipe. `Autocomplete` re-selects on its own across that blur,
     * because it alone can tell a value read back from a filter being typed.
     */
    if (enteredEditor instanceof HTMLInputElement || enteredEditor instanceof HTMLTextAreaElement) {
      enteredEditor.select();
    }

    /**
     * A blur this editor never asked for.
     *
     * `OutPortal` RE-PARENTS this node — same element, moved — and moving a focused
     * node blurs it. Nothing inside `contents` changes, so there is no mutation to
     * observe and no re-render to re-run this effect; `document.activeElement` just
     * falls to <body> and stays. `resolveActiveCell` then reads no editing cell and
     * declines every Tab, so the caret is gone until the user clicks (TAS-3791).
     *
     * Restoring is gated on the GRID's own state — this row still editing, this cell
     * still the focused one — never a DOM guess.
     *
     * That gate is also what keeps this from fighting Tab NAVIGATION, which blurs the
     * input deliberately. `goToCell` moves the focused cell synchronously, before the
     * microtask below runs, so a navigating blur already fails the gate. An explicit
     * "navigation in progress" flag was tried and measured STRICTLY WORSE: it
     * suppresses the restore for a fixed window, and at the quantity -> unitAmount
     * boundary the next editor had not taken focus inside it, so focus fell to <body>
     * mid-row. The grid's own state is the more precise signal.
     */
    const onFocusOut = (event: FocusEvent) => {
      // `relatedTarget` is `<body>`, NOT null, when focus is LOST rather than moved — the
      // browser reports the fallback focus owner.
      const focusWentSomewhereReal = event.relatedTarget !== null && event.relatedTarget !== document.body;

      if (focusWentSomewhereReal) {
        return;
      }

      queueMicrotask(() => {
        // The same accessor the core uses (`focusFirstEditableCell`); the grid
        // selectors take an apiRef, not this bare api object.
        const focusedCell = api.state?.focus?.cell;
        const stillOurs =
          api.getRowMode(rowId) === GridRowModes.Edit && focusedCell?.id === rowId && focusedCell?.field === field;

        if (stillOurs && document.activeElement === document.body) {
          editorIn()?.focus({ preventScroll: true });
        }
      });
    };

    /**
     * A click on the cell UNDER an open editor hands focus straight back to the editor.
     *
     *   editor open, focused ─ click its cell ─▶ cell <div> takes focus ─▶ editor ◄── here
     *
     * The editor floats in a portal, so a click on its own cell moves focus OUT of it while the
     * row stays in edit. A whole-grid re-render used to put it back as a side effect; the grid
     * now re-renders only what changed (STG-R2-SO-2), so it is done on purpose, behind the same
     * grid-state gate as the restore above.
     */
    const onCellFocusIn = (event: FocusEvent) => {
      if (event.target !== api.getCellElement(rowId, field)) {
        return;
      }

      queueMicrotask(() => {
        const focusedCell = api.state?.focus?.cell;
        const stillOurs =
          api.getRowMode(rowId) === GridRowModes.Edit && focusedCell?.id === rowId && focusedCell?.field === field;

        if (stillOurs && document.activeElement === event.target) {
          editorIn()?.focus({ preventScroll: true });
        }
      });
    };

    contents.addEventListener('focusout', onFocusOut);
    document.addEventListener('focusin', onCellFocusIn, true);

    return () => {
      contents.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('focusin', onCellFocusIn, true);
    };
  }, [api, contentsRef, field, rowId, selector]);

  return children;
};
